// Library Imports
import { useState, useEffect } from "react";
import { Tooltip, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";

// Local Imports
import Checkbox from "../../../Common/Components/Checkbox";
import { AUTOMATION_LISTS } from "constants/types/automationTypes";
import { IListData, ILists, ParamsObjectType } from "modules/Automation/interfaces";
import SentEmailSearch from "modules/Campaigns/Emails/Components/SentEmailSearch";
import EmptyTabInfo from "modules/Content/Shared/EmptyTabInfo";
import { RootState } from "store";
import { ControlNameEnum } from "../../interfaces";
import {
  addNode,
  clearDropArea,
  addActionModal,
  editNode,
  listsAPI,
  setSelectedlistIdsAction,
} from "../../actions";
import { findDropAreaData } from "../../utils";
import InfiniteLoader from "modules/Common/Components/InfiniteLoader";
import styled from "styled-components";

type IListDataProps = {
  id: string;
  enableSaveButton: (value: boolean) => void;
};

const ListsData = (props: IListDataProps) => {
  const { id, enableSaveButton } = props;
  const { Paragraph } = Typography;
  const [ellipsisState, setEllipsistate] = useState(false);
  // Redux States
  const lists: ILists = useSelector((state: RootState) => state.automation.lists);
  const listsLoading: boolean = useSelector((state: RootState) => state.automation.lists.loading);
  const totalCount: number = useSelector((state: RootState) => state.automation.lists.totalCount);
  const itemData = useSelector((state: RootState) => state.automation.workflow.flow[id]?.detail);
  const modal = useSelector((state: RootState) => state.automation.modals.addAction);
  const actionModalVisible: boolean = useSelector((state: RootState) => state.automation.modals.addAction.visible);
  const dispatch = useDispatch();

  // Local States
  const [isSearchedList, setIsSearchedList] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedListIds, setSelectedListIds] = useState<number[]>([]);

  // Local Variables
  let listsData: IListData[] = isSearchedList ? lists.searchedData : lists.data;

  // get Pagination
  const getPagination = (reset?: boolean, searchValue?: string) => {
    let params: ParamsObjectType = {
      limit: 15,
      offset: reset ? 0 : listsData.length,
    };
    if (searchValue) {
      params.list_name = searchValue;
    }
    return params;
  };

  // Component Logic
  const updateListSearchTermInStore = (value: string) => {
    dispatch({
      type: AUTOMATION_LISTS.SET_SEARCH_TERM,
      payload: value,
    });
    dispatch({ type: AUTOMATION_LISTS.RESET_SEARCH_DATA });
    // if (value.trim()) listsAPI(value);
  };

  const listSearchHandler = (value: string) => {
    updateListSearchTermInStore(value);
    setSearchTerm(value);
    setIsSearchedList(true);
    if (!listsLoading) listsAPI(getPagination(true, value));
  };

  const resetListSearchHandler = () => {
    updateListSearchTermInStore("");
    setSearchTerm("");
    setIsSearchedList(false);
  };

  // useEffect to set the data of edit Node
  const isListModalOpen = modal.visible === true && modal.tab === ControlNameEnum.ADD_LIST;
  let dependencies = itemData && isListModalOpen;
  useEffect(() => {
    if (isListModalOpen && itemData) {
      setSelectedListIds([]);
      setSelectedlistIdsAction([]);
      let ids = itemData?.list?.map((list: IListData) => list.list_id);
      if (ids?.length) {
        setSelectedListIds(ids);
        setSelectedlistIdsAction(ids);
      }
      enableSaveButton(ids?.length > 0);
    } else if (isListModalOpen && selectedListIds?.length > 0) {
      setSelectedListIds([]);
      setSelectedlistIdsAction([]);
      enableSaveButton(false);
    }
  }, [dependencies]);

  useEffect(() => {
    if (modal.visible === false && selectedListIds.length > 0) {
      enableSaveButton(false);
      setSelectedListIds([]);
      setSelectedlistIdsAction([]);
    }
  }, [modal]);

  const checkboxHandler = (isChecked: boolean, clickedList: IListData) => {
    let checkedListIds: number[] = [];
    if (isChecked) {
      checkedListIds = [...selectedListIds, clickedList.list_id];
    } else {
      checkedListIds = selectedListIds.filter((id) => id !== clickedList.list_id);
    }
    setSelectedListIds(checkedListIds);
    setSelectedlistIdsAction(checkedListIds);
    enableSaveButton(checkedListIds.length > 0);
  };

  const submitHandler = (event: any) => {
    event.preventDefault();
    let obj = findDropAreaData(ControlNameEnum.ADD_LIST);
    let selectedList: any = listsData.filter((list) => selectedListIds.includes(list.list_id));
    let title = `: ${selectedList.map((list: IListData) => ` ${list.list_name}`)}`;
    if (id) {
      // edit Node
      dispatch(
        editNode(id, {
          ...itemData,
          list: selectedList,
          nodeTitle: title,
        })
      );
    } else {
      dispatch(
        addNode({
          ...obj,
          list: selectedList,
          nodeTitle: title,
        })
      );
    }
    setSelectedListIds([]);
    setSelectedlistIdsAction([]);
    enableSaveButton(false);
    dispatch(addActionModal(false));
    dispatch(clearDropArea());
  };

  useEffect(() => {
    if (!listsLoading && lists.data.length === 0) listsAPI(getPagination(true));
  }, []);

  const handleNext = () => {
    if (!listsLoading) listsAPI(getPagination(false, searchTerm));
  };

  useEffect(() => {
    if (!actionModalVisible) {
      setSelectedListIds([]);
      setSelectedlistIdsAction([]);
    }
  }, [actionModalVisible]);

  return (
    <Wrapper>
      <div className="search-wrapper">
        <SentEmailSearch
          handleSearch={listSearchHandler}
          reset={resetListSearchHandler}
          placeholder="Search lists"
        />
      </div>
      {lists.loading && listsData.length === 0 && <EmptyDiv description="Loading..." />}
      {listsData.length > 0 ? (
        <form id={`${ControlNameEnum.ADD_LIST}`} onSubmit={submitHandler}>
          <ul className="email-list" id="contact_lists_wf">
            <InfiniteLoader
              dataLength={listsData.length + 1}
              // hasMore={
              //   isSearchedList ? totalCount > listsData.length : totalCount > listsData.length
              // }
              hasMore={true}
              next={handleNext}
              scrollableTarget={"contact_lists_wf"}
              loader={
                listsLoading && listsData.length > 0 ? (
                  <EmptyDiv className={"paginated"} description={"Loading..."} />
                ) : (
                  <></>
                )
              }
            >
              {listsData.map((list: IListData) => {
                return (
                  <li key={list.list_id}>
                    <label className="w-100 automation-list-item">
                      <Checkbox
                        checked={selectedListIds.includes(list.list_id)}
                        onChange={(e: any) => checkboxHandler(e.target.checked, list)}
                      />
                      <Tooltip placement="top" title={ellipsisState ? list.list_name : undefined}>
                        <Paragraph className="list-name" ellipsis={{ onEllipsis: setEllipsistate }}>
                          {list.list_name}
                        </Paragraph>
                      </Tooltip>
                    </label>
                  </li>
                );
              })}
            </InfiniteLoader>
          </ul>
          {selectedListIds.length ? (
            <div className="list-count">
              <span> {selectedListIds.length} list(s) selected</span>
            </div>
          ) : null}
        </form>
      ) : listsData.length === 0 && !listsLoading ? (
        <EmptyDiv description={isSearchedList ? "No lists found against your search." : "No lists available."} />
      ) : null}
    </Wrapper>
  );
};

export default ListsData;

const EmptyDiv = styled(EmptyTabInfo)`
  width: 100%;
  height: 415px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    height: 320px;
  }
  &.paginated {
    height: auto;
  }
`;

const Wrapper = styled.div`
  /* display: flex;
  flex-direction: column; */
  .automation-list-item {
    display: flex;
    align-items: center;
  }
  .list-name {
    height: auto;
  }
`;
